import log from 'loglevel'
import {
  DOCUMENT_TYPE,
  IDsQuery,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  ModelRecord,
  ModelType,
} from '@trustero/trustero-api-web/lib/model/model_pb'
import {
  ReceptorID,
  ReceptorRecord,
} from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import React, { Dispatch } from 'react'
import { Cache } from 'swr'
import {
  AccountPromiseClient,
  UserPromiseClient,
} from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { ReceptorPromiseClient } from '@trustero/trustero-api-web/lib/agent/receptor_grpc_web_pb'
import { Message } from 'google-protobuf'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { ServicePromiseClient } from '@trustero/trustero-api-web/lib/service/service_grpc_web_pb'
import { ModelStatsServicePromiseClient } from '@trustero/trustero-api-web/lib/model/stats_grpc_web_pb'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import { ACTIVITY_ACTOR_TYPE } from '@trustero/trustero-api-web/lib/activity/activity_pb'
import {
  Identifier,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
} from '../app/AppAuth/rpcAuthorization'
import { KeyedPromises } from '../Utils'
import { Content, contentInitialState, Model } from '../context/Content/defs'
import { Overview, Receptor, Service } from '../xgenerated'
import { AuthAction, AuthRecord } from '../context/authContext'
import { GrpcCall } from '../components/async/utils'
import { isGrpcError } from '../Utils/isGrpcError'
import { mutateControlDependencies } from '../components/async/model'
import { CITATION_TO_MODEL_TYPE } from '../pages/Activities/ActivitiesPage.constants'
import AttachmentAdapter, {
  AddDocumentType,
  convertDocToObject,
  DeleteDocumentType,
  DocumentObj,
} from './AttachmentAdapter'
import {
  callApiAndUpdateContent,
  createLocalModel,
} from './dataModelAdapterUtils'
import { isOverview } from './typeUtils'
import ActivityFeedAdapter, { ActivityFeedEntry } from './ActivityFeedAdapter'
import { authorizedGrpcClientFactory } from './grpcClient'

const logger = log.getLogger('adapter')

function _initLocalModelFromServerModel(
  localModelIn: Model,
  modelRecord?: ModelRecord.AsObject,
): Model {
  if (!modelRecord) {
    // no server-side copy of this object exists
    return localModelIn
  }
  let localModel = localModelIn
  localModel = {
    ...localModel,
    ...modelRecord,
    id: localModel.id,
    ...('timestamp' in localModel
      ? { timestamp: modelRecord.updatedat.valueOf() }
      : {}),
    ...{ owneremail: modelRecord.owneremail || 'Unassigned' },
  }
  if (
    'content' in localModel &&
    !('custom' in localModel) &&
    Boolean(modelRecord.description)
  ) {
    // initial objects already have content which isn't saved in the
    // backend, so we update only if the description is not empty
    localModel.content = modelRecord.description
  }
  if ('custom' in localModel) {
    // this is a content object added by the customer, not
    // one included by Trustero.  That means the extra data
    // is in the description field.
    try {
      const objDescription = JSON.parse(modelRecord.description)
      Object.assign(localModel, objDescription) // nosemgrep
    } catch (err) {
      logger.error('Error loading custom obj:', localModel.id, err)
    }
  }

  return localModel
}

const _initLocalModelFromDocuments = (
  modelType: MODEL_TYPE,
  localModel: Model,
  documents: DocumentObj[],
) => {
  const filterByTypeAndReverse = (modelId: string, docType: DOCUMENT_TYPE) =>
    documents
      .filter(
        (doc) => doc.subjectmodelid === modelId && doc.doctype === docType,
      )
      .reverse()

  // update each of the document fields
  switch (modelType) {
    case MODEL_TYPE.POLICY:
      break
    case MODEL_TYPE.OVERVIEW: {
      if (!isOverview(localModel)) {
        break
      }
      // there can be only one attachment for Overview questions, so take
      // the first one.
      const overviewDocs = filterByTypeAndReverse(
        localModel.id,
        DOCUMENT_TYPE.COMPANY_INFO,
      )
      localModel.document = overviewDocs.shift()
      if (overviewDocs.length > 0) {
        logger.error(
          'Found extra documents for overview',
          localModel.id,
          '=',
          overviewDocs,
        )
      }
      break
    }
    default:
      // no documents for the rest
      break
  }
}

function mergeOverviewDocuments(
  overviews: Overview[],
  documents: DocumentObj[],
) {
  const docMap = documents.reduce((agg, curr) => {
    agg[curr.subjectmodelid] = curr
    return agg
  }, {} as { [key: string]: DocumentObj }) // skipcq: JS-0369

  return overviews.map((o) => ({
    ...o,
    document: docMap[o.modelid],
  }))
}

const getCustomServices = (
  content: Content,
  staticServices: Service[],
  dynamicServices: ModelRecord.AsObject[],
): Service[] => {
  const staticServiceIds = staticServices.map((service) => service.modelid)
  const customServiceModels = dynamicServices.filter(
    (service) => !staticServiceIds.includes(service.modelid),
  )

  return customServiceModels.reduce((acc: Service[], serviceModel) => {
    const model = createLocalModel(
      content,
      MODEL_TYPE.SERVICE,
      serviceModel.modelid,
    )
    model && // skipcq: JS-0354
      acc.push(_initLocalModelFromServerModel(model, serviceModel) as Service) // skipcq: JS-0354
    return acc
  }, [])
}

/**
 * Adapter class which writes changes back to ntrced and then fetches the
 * updated objects to place into our "global UI cache" (ContentContext).
 */
// skipcq: JS-0327
export default class DataModelAdapter {
  // skipcq: JS-0327
  static async reload(
    authCtx: AuthRecord | null,
    authDispatch: React.Dispatch<AuthAction> | null,
    contentDispatch: React.Dispatch<Content>,
    cache: Cache,
    isCurrent: () => boolean = () => true,
  ): Promise<void> {
    contentDispatch({ deltaUpdateRunning: true } as Content)
    // Cache keys embed the authorization scope. Drop only other scopes'
    // entries: mounted hooks have already written theirs.
    if (cache instanceof Map) {
      const scope = JSON.stringify(getAuthorizationCacheScope(authCtx))
      Array.from(cache.keys())
        .filter((key) => !key.includes(scope))
        .forEach((key) => cache.delete(key))
    }
    const staticContent = await loadStaticContent()
    if (!isCurrent()) return
    const modelIds = {
      services: staticContent.services.map((p) => p.modelid),
      receptors: staticContent.receptors.map((p) => p.id),
      overviews: staticContent.overviews.map((p) => p.id),
    }
    const dynamicContent = await loadDynamicContent(
      modelIds,
      authCtx,
      authDispatch,
    )
    if (!isCurrent()) return
    const dynamicModelIdMap = [
      ...dynamicContent.services,
      ...dynamicContent.receptors,
    ].reduce((aggr, curr) => {
      aggr.set(curr.modelid, curr)
      return aggr
    }, new Map<string, ModelRecord.AsObject | ReceptorRecord.AsObject>())

    const updatedStaticServices = staticContent.services.map((p) =>
      _initLocalModelFromServerModel(
        p,
        dynamicModelIdMap.get(p.id) as ModelRecord.AsObject,
      ),
    )
    const customServices = getCustomServices(
      contentInitialState(),
      staticContent.services,
      dynamicContent.services,
    )

    contentDispatch({
      ...staticContent,
      ...dynamicContent,
      services: [...updatedStaticServices, ...customServices],
      overviews: mergeOverviewDocuments(
        staticContent.overviews,
        dynamicContent.documents,
      ),
      receptors: staticContent.receptors.map((p) => ({
        ...(dynamicModelIdMap.get(p.id) as ReceptorRecord.AsObject),
        ...p,
      })),
      deltaUpdateRunning: false,
      loaded: true,
    } as unknown as Content)
  }

  // Read the activity log to figure out which objects have changed since the last
  // update we have locally seen, and invalidate the caches that cover them.
  static async getDeltaUpdate(
    lastUpdateStamp: number,
    content: Content,
    authCtx: AuthRecord | null,
    authDispatch: React.Dispatch<AuthAction> | null,
    methodMutator: (asyncCall: unknown) => Promise<unknown>,
    // Kept for signature compatibility with callApiAndUpdateContent. Invalidations are
    // now all method-level; nothing here needs a per-request mutator.
    _methodRequestMutator?: (
      asyncCall: GrpcCall<Message, Message>,
      request: Message,
    ) => Promise<Message>,
  ): Promise<Content | undefined> {
    let newEntries
    try {
      newEntries = await ActivityFeedAdapter.getFeed(
        lastUpdateStamp,
        authCtx,
        authDispatch,
      )
    } catch (err) {
      if (
        isGrpcError(err) &&
        err.code === 2 &&
        err.message === 'Http response at 400 or 500 level'
      ) {
        return
      }
      throw err
    }
    if (newEntries.length === 0) {
      return
    }
    logger.debug('Activity feed entries = ', newEntries)

    const othersEntries = entriesNotMadeBy(newEntries, authCtx?.email)

    // Activity log entries carry citations rather than a single subject, so group
    // the cited ids by the model type each citation maps to. Entries flagged
    // is_account_change have no citable object and go in one coarse bucket.
    const changedIdsByModelType = {} as Record<MODEL_TYPE, Set<string>>
    let hasAccountChange = false
    othersEntries.forEach((entry) => {
      if (entry.isAccountChange) {
        hasAccountChange = true
      }
      entry.citations.forEach((citation) => {
        const modelType = CITATION_TO_MODEL_TYPE[citation.type] as
          | MODEL_TYPE
          | undefined
        if (modelType === undefined) return
        if (!changedIdsByModelType[modelType]) {
          changedIdsByModelType[modelType] = new Set<string>()
        }
        changedIdsByModelType[modelType].add(citation.id)
      })
    })

    await doCacheInvalidations(
      changedIdsByModelType,
      hasAccountChange,
      methodMutator,
    )

    // Entries are returned newest-first, so the head carries the new watermark.
    return {
      loaded: true,
      lastUpdateStamp: newEntries[0].createdAtMs,
    } as Content
  }

  static deltaUpdate(
    authCtx: AuthRecord,
    authDispatch: React.Dispatch<AuthAction>,
    content: Content,
    contentDispatch: React.Dispatch<Content>,
    cache: Cache,
    methodMutator: (asyncCall: unknown) => Promise<unknown>,
    methodRequestMutator: (
      asyncCall: GrpcCall<Message, Message>,
      request: Message,
    ) => Promise<Message>,
  ): void {
    if (content.deltaUpdateRunning) {
      // don't do anything if we're already running!
      return
    }
    // launch the actual delta update in the background
    const workFunc = async () => {
      contentDispatch({ deltaUpdateRunning: true } as Content)
      if (!content.loaded) {
        // don't be dumb - if the content isn't loaded from scratch, do
        // that now
        await DataModelAdapter.reload(
          authCtx,
          authDispatch,
          contentDispatch,
          cache,
        )
      } else {
        await callApiAndUpdateContent(
          async () => {
            return null
          },
          content,
          contentDispatch,
          authCtx,
          authDispatch,
          methodMutator,
          methodRequestMutator,
        )
      }
      contentDispatch({ deltaUpdateRunning: false } as Content)
    }
    workFunc()
  }

  // MODEL Actions (shared across several models)

  static modelEditDocument(
    authCtx: AuthRecord,
    authDispatch: Dispatch<AuthAction>,
    content: Content,
    contentUpdate: Dispatch<Content>,
    arg: AddDocumentType & DeleteDocumentType,
    methodMutator: (asyncCall: unknown) => Promise<unknown>,
    methodRequestMutator: (
      asyncCall: GrpcCall<Message, Message>,
      request: Message,
    ) => Promise<Message>,
  ): Promise<DocumentObj | undefined> {
    return callApiAndUpdateContent(
      async () => {
        const addedDocument = await AttachmentAdapter.addDocument(
          arg,
          authCtx,
          authDispatch,
        )
        await AttachmentAdapter.deleteDocument(arg, authCtx, authDispatch)
        return addedDocument
      },
      content,
      contentUpdate,
      authCtx,
      authDispatch,
      methodMutator,
      methodRequestMutator,
    )
  }
}

/**
 * The feed entries for changes someone else made. Our own changes are invalidated inline
 * by the mutation path (see callApiAndUpdateContent), so they are skipped. An AI agent
 * acting for us logs under our email too, but its writes never went through that path,
 * so only our own USER entries are skipped.
 */
export const entriesNotMadeBy = (
  entries: ActivityFeedEntry[],
  selfActor: string | undefined,
): ActivityFeedEntry[] =>
  selfActor
    ? entries.filter(
        (entry) =>
          entry.actor !== selfActor ||
          entry.actorType !== ACTIVITY_ACTOR_TYPE.USER,
      )
    : entries

/**
 * Invalidate SWR caches based on activity events generated by others
 */
/**
 * Invalidate the caches covering objects another actor changed.
 *
 * `changedIdsByModelType` holds the cited object ids grouped by model type.
 * `hasAccountChange` covers account-level ("general") changes — account settings,
 * overview, and other edits with no citable object of their own. Those are rarely
 * viewed or changed, so they share one coarse bucket rather than being tracked per
 * object.
 */
export async function doCacheInvalidations(
  changedIdsByModelType: Record<MODEL_TYPE, Set<string>>,
  hasAccountChange: boolean,
  methodMutator: (asyncCall: unknown) => Promise<unknown>,
): Promise<void> {
  const promises: Promise<unknown>[] = []
  const getSubjectIds = (modelType: MODEL_TYPE): Set<string> =>
    changedIdsByModelType[modelType] ?? new Set<string>()

  if (hasAccountChange) {
    // Account/overview/general bucket: the document queries the overview pages read,
    // plus the account records themselves.
    promises.push(
      methodMutator(AttachmentPromiseClient.prototype.getDocumentsBySubjectIDs),
    )
    promises.push(
      methodMutator(AttachmentPromiseClient.prototype.getDocumentCounts),
    )
    promises.push(
      methodMutator(AttachmentPromiseClient.prototype.getDocumentByOID),
    )
    promises.push(
      methodMutator(AttachmentPromiseClient.prototype.getDocumentsByOIDs),
    )
    promises.push(methodMutator(AccountPromiseClient.prototype.get))
    promises.push(methodMutator(AccountPromiseClient.prototype.getAccounts))
  }
  if (getSubjectIds(MODEL_TYPE.POLICY).size > 0) {
    promises.push(methodMutator(PolicyPromiseClient.prototype.get))
    promises.push(methodMutator(PolicyPromiseClient.prototype.listIds))
    promises.push(methodMutator(PolicyPromiseClient.prototype.getControls))
    promises.push(methodMutator(PolicyPromiseClient.prototype.getFrameworks))
    promises.push(
      methodMutator(ModelStatsServicePromiseClient.prototype.getTrusteroScore),
    )
  }
  if (getSubjectIds(MODEL_TYPE.CONTROL).size > 0) {
    promises.push(methodMutator(ModelPromiseClient.prototype.listControls))
    promises.push(mutateControlDependencies(methodMutator))
  }

  if (getSubjectIds(MODEL_TYPE.AUDITBOT_RUN).size > 0) {
    // AUDIT_RUN citations come from actions that rewrite a control test's source
    // hashes (Mark Results Current), so it is the readiness and control-test reads
    // that go stale for everyone else — not the control list or its stats.
    promises.push(
      methodMutator(AuditBotPromiseClient.prototype.getAuditControlTestRuns),
    )
    promises.push(
      methodMutator(AuditBotPromiseClient.prototype.getAuditReadiness),
    )
  }

  if (getSubjectIds(MODEL_TYPE.SERVICE).size > 0) {
    // SERVICE citations carry the services-table id, which is what the services
    // pages query by, so invalidate those reads (not the legacy models-keyed ones).
    promises.push(methodMutator(ServicePromiseClient.prototype.listServices))
    promises.push(methodMutator(ServicePromiseClient.prototype.getServiceById))
    promises.push(methodMutator(ModelPromiseClient.prototype.listIds))
  }

  if (getSubjectIds(MODEL_TYPE.RECEPTOROBJ).size > 0) {
    promises.push(methodMutator(ReceptorPromiseClient.prototype.getReceptors))
    promises.push(methodMutator(ReceptorPromiseClient.prototype.listIds))
  }
  if (getSubjectIds(MODEL_TYPE.USEROBJ).size > 0) {
    promises.push(methodMutator(UserPromiseClient.prototype.getUsersInAccount))
    promises.push(methodMutator(UserPromiseClient.prototype.get))
  }

  await Promise.all(promises)
}

type StaticContent = {
  overviews: Overview[]
  receptors: Receptor[]
  services: Service[]
}

async function loadStaticContent(): Promise<StaticContent> {
  const promises = {
    overviews: import('../xgenerated/overview')
      .then((mod) => mod.default)
      .catch((err) => logger.error('Error importing overview data:', err)),
    receptors: import('../xgenerated/receptor')
      .then((mod) => mod.default)
      .catch((err) => logger.error('Error importing receptor data:', err)),
    services: import('../xgenerated/service')
      .then((mod) => mod.default)
      .catch((err) => logger.error('Error importing services data:', err)),
  }
  const results = (await KeyedPromises(promises)) as StaticContent
  return {
    overviews: results.overviews ?? [],
    receptors: results.receptors ?? [],
    services: results.services ?? [],
  }
}

type DynamicContent = {
  services: ModelRecord.AsObject[]
  receptors: ReceptorRecord.AsObject[]
  documents: DocumentObj[]
  lastUpdateStamp: number
}

async function loadDynamicContent(
  models: { services: string[]; receptors: string[]; overviews: string[] },
  authCtx: AuthRecord | null,
  authDispatch: React.Dispatch<AuthAction> | null,
): Promise<DynamicContent> {
  const modelClient = authorizedGrpcClientFactory(
    ModelPromiseClient,
    authCtx,
    authDispatch,
  )
  const receptorClient = authorizedGrpcClientFactory(
    ReceptorPromiseClient,
    authCtx,
    authDispatch,
  )
  const attachmentClient = authorizedGrpcClientFactory(
    AttachmentPromiseClient,
    authCtx,
    authDispatch,
  )

  // We request the newest activity log entry to get a base timestamp from which to
  // start our delta syncing. That means this request needs to be made before
  // fetching models from ntrced to avoid race conditions.
  const lastEntries = ActivityFeedAdapter.getFeed(
    0,
    authCtx,
    authDispatch,
  ).catch((error) => {
    logger.warn('Unable to initialize optional activity feed:', error)
    return []
  })
  const overviewDocumentsQuery = new IDsQuery().setSubjectidsList(
    models.overviews.map((p) =>
      new Identifier().setModelid(p).setModeltype(MODEL_TYPE.OVERVIEW),
    ),
  )
  const [servicesResult, receptorsResult, documentsResult] =
    await Promise.allSettled([
      getRpcAccess(
        ModelPromiseClient.prototype.getByModelType,
        authCtx,
        new ModelType().setModeltype(MODEL_TYPE.SERVICE),
      ) === 'allowed'
        ? modelClient.getByModelType(
            new ModelType().setModeltype(MODEL_TYPE.SERVICE),
          )
        : undefined,
      getRpcAccess(ReceptorPromiseClient.prototype.getReceptors, authCtx) ===
      'allowed'
        ? receptorClient.getReceptors(new ReceptorID())
        : undefined,
      getRpcAccess(
        AttachmentPromiseClient.prototype.getDocumentsBySubjectIDs,
        authCtx,
        overviewDocumentsQuery,
      ) === 'allowed'
        ? attachmentClient.getDocumentsBySubjectIDs(overviewDocumentsQuery)
        : undefined,
    ])

  // These are optional startup enrichments. Each feature owns its loading and
  // error UI; a failure here must not block unrelated pages from opening.
  const optionalResult = <T>(
    result: PromiseSettledResult<T>,
    feature: string,
  ): T | undefined => {
    if (result.status === 'fulfilled') return result.value
    logger.warn(`Unable to initialize optional ${feature}:`, result.reason)
    return undefined
  }
  const services = optionalResult(servicesResult, 'services')
  const receptors = optionalResult(receptorsResult, 'receptors')
  const documents = optionalResult(documentsResult, 'company documents')
  return {
    services: services?.toObject().recordsList || [],
    receptors: receptors?.toObject().receptorsList || [],
    documents:
      documents?.getDocumentsList().map((p) => convertDocToObject(p)) || [],
    lastUpdateStamp: (await lastEntries)[0]?.createdAtMs ?? 0,
  }
}
